import React from "react";
import useCurrentCompanyContext from "hooks/useCurrentCompanyContext";
import { InputText } from "primereact/inputtext";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import { Divider } from "primereact/divider";
import { T } from "services/lang/LanguageService";
import { InputNumber } from "primereact/inputnumber";
import "./VideoPlayerEditor.scss";
import { DashboardEditorProps } from "components/ComponentType";

export interface VideoPlayerConfig {
  useUniversalUrl: boolean;
  streamServerUrl?: string;
  streamPath?: string;
  universalVideoUrl?: string;
  height?: number;
}

const VideoPlayerEditor = ({ config, onConfigChange }: DashboardEditorProps<VideoPlayerConfig>) => {
  const companyContext = useCurrentCompanyContext();

  const handleUseUniversalUrlChange = (e: InputSwitchChangeEvent) => {
    if (!e.value) {
      onConfigChange({ ...config, universalVideoUrl: undefined, useUniversalUrl: e.value });
      return;
    }
    onConfigChange({
      ...config,
      useUniversalUrl: e.value,
      streamServerUrl: undefined,
      streamPath: undefined,
    });
  };

  const currentURL = config.useUniversalUrl
    ? config.universalVideoUrl
    : `${config.streamServerUrl ?? ""}/${companyContext?.companyReference}/${config.streamPath ?? ""}/?token=...`;

  return (
    <div className="flex flex-column gap-3 video-player-editor">
      <div className="flex flex-row justify-content-between">
        <div className="flex flex-column gap-2">
          <label htmlFor="useUniversalUrl">{T("label.use-universal-video-url")}</label>
          <InputSwitch
            id="useUniversalUrl"
            checked={config.useUniversalUrl}
            onChange={handleUseUniversalUrlChange}
          />
        </div>
        <div className="flex flex-column gap-2">
          <label htmlFor="height">{T("label.custom-height")} (px)</label>
          <InputNumber
            id="height"
            value={config.height ?? undefined}
            onChange={e => onConfigChange({ ...config, height: e.value ?? undefined })}
          />
        </div>
      </div>
      {config.useUniversalUrl && (
        <div className="flex flex-column gap-2">
          <label htmlFor="universalUrl">{T("label.universal-video-url")}</label>
          <InputText
            id="universalUrl"
            type="text"
            placeholder={T("label.universal-url")}
            value={config.universalVideoUrl}
            onChange={e => onConfigChange({ ...config, universalVideoUrl: e.target.value })}
          />
        </div>
      )}
      {!config.useUniversalUrl && (
        <>
          <div className="flex flex-column gap-2">
            <label htmlFor="streamServerUrl">{T("label.stream-server-url")}</label>
            <InputText
              id="streamServerUrl"
              type="text"
              placeholder={T("label.stream-server-url")}
              value={config.streamServerUrl}
              onChange={e => onConfigChange({ ...config, streamServerUrl: e.target.value })}
            />
          </div>
          <div className="flex flex-column gap-2">
            <label htmlFor="streamPath">{T("label.stream-path")}</label>
            <InputText
              id="streamPath"
              type="text"
              placeholder={T("label.stream-path")}
              value={config.streamPath}
              onChange={e => onConfigChange({ ...config, streamPath: e.target.value })}
            />
          </div>
        </>
      )}
      <Divider />
      <div className="flex flex-column gap-2">
        <label htmlFor="currentURL">
          {config.useUniversalUrl ? T("label.final-video-url") : T("label.resolved-stream-url")}
        </label>
        <div id="currentURL" className="current-url">
          {currentURL}
        </div>
      </div>
    </div>
  );
};

export default VideoPlayerEditor;
